import React, { useEffect, useState, useRef, useCallback } from 'react';
import {
  Box, Flex, HStack, VStack, Button, Input, Text, Icon, SimpleGrid, Wrap, WrapItem,
  Tag, IconButton, Spinner, Divider, FormControl, FormLabel,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton,
  Table, Thead, Tbody, Tr, Th, Td,
  AlertDialog, AlertDialogBody, AlertDialogFooter, AlertDialogHeader,
  AlertDialogContent, AlertDialogOverlay,
  useDisclosure, useToast,
} from '@chakra-ui/react';
import { ChevronLeftIcon, ChevronRightIcon, AddIcon } from '@chakra-ui/icons';
import { MdEventNote, MdAccessTime } from 'react-icons/md';
import {
  ImmCita, ImmDisponibilidad,
  listarCitas, reagendarCita, cancelarCita, atenderCita, disponibilidadPsicologia,
} from '../../../api/immApi';
import { IMM_COLOR, IMM_COLOR_HOVER, EstatusBadge, fmt } from '../_shared';
import FormularioCita from './FormularioCita';
import ModalSesion from './ModalSesion';

const hoy = () => new Date().toISOString().split('T')[0];

/** Desplaza una fecha YYYY-MM-DD por delta días (sin problemas de zona horaria). */
const shiftDay = (f: string, delta: number) => {
  const [y, m, d] = f.split('-').map(Number);
  const dt = new Date(y, m - 1, d + delta);
  const yy = dt.getFullYear();
  const mm = String(dt.getMonth() + 1).padStart(2, '0');
  const dd = String(dt.getDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
};

const fechaLarga = (f: string) => {
  const [y, m, d] = f.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('es-MX', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  });
};

const CalendarioPsicologia: React.FC = () => {
  const toast = useToast();
  const cancelRef = useRef<any>(null);

  const [fecha, setFecha] = useState<string>(hoy());
  const [citas, setCitas] = useState<ImmCita[]>([]);
  const [disp, setDisp] = useState<ImmDisponibilidad | null>(null);
  const [cargando, setCargando] = useState(false);

  // upcoming (tabla secundaria)
  const [proximas, setProximas] = useState<ImmCita[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const [selected, setSelected] = useState<ImmCita | null>(null);
  const [procesando, setProcesando] = useState(false);

  const nuevaD = useDisclosure();
  const sesionD = useDisclosure();
  const reagendarD = useDisclosure();
  const cancelarD = useDisclosure();

  const [rea, setRea] = useState<{ fecha: string; horaInicio: string; horaFin: string; psicologo: string }>({
    fecha: '', horaInicio: '', horaFin: '', psicologo: '',
  });

  const fetchDia = useCallback(async () => {
    setCargando(true);
    try {
      const [c, d] = await Promise.all([
        listarCitas({ fecha }),
        disponibilidadPsicologia(fecha),
      ]);
      setCitas(c);
      setDisp(d);
    } catch {
      setCitas([]);
      setDisp(null);
    } finally {
      setCargando(false);
    }
  }, [fecha]);

  const fetchProximas = useCallback(async () => {
    try {
      setProximas(await listarCitas());
    } catch {
      setProximas([]);
    }
  }, []);

  useEffect(() => { fetchDia(); }, [fetchDia]);
  useEffect(() => { fetchProximas(); }, [fetchProximas]);

  const refrescar = () => { fetchDia(); fetchProximas(); };

  const citasOrdenadas = [...citas].sort((a, b) =>
    (a.horaInicio || '').localeCompare(b.horaInicio || ''));

  const handleGuardado = () => { nuevaD.onClose(); refrescar(); };

  const abrirReagendar = (c: ImmCita) => {
    setSelected(c);
    setRea({
      fecha: c.fecha ? c.fecha.split('T')[0] : fecha,
      horaInicio: c.horaInicio || '',
      horaFin: c.horaFin || '',
      psicologo: c.psicologo || '',
    });
    reagendarD.onOpen();
  };

  const handleReagendar = async () => {
    if (!selected) return;
    setProcesando(true);
    try {
      await reagendarCita(selected.id, {
        fecha: rea.fecha || undefined,
        horaInicio: rea.horaInicio || undefined,
        horaFin: rea.horaFin || undefined,
        psicologo: rea.psicologo || undefined,
      });
      toast({ title: 'Cita reagendada', status: 'success', duration: 3000 });
      reagendarD.onClose();
      refrescar();
    } catch (error: any) {
      toast({ title: 'Error', description: error?.response?.data?.message || 'No se pudo reagendar.', status: 'error', duration: 4000 });
    } finally {
      setProcesando(false);
    }
  };

  const handleCancelarConfirmar = async () => {
    if (!selected) return;
    setProcesando(true);
    try {
      await cancelarCita(selected.id);
      toast({ title: 'Cita cancelada', status: 'success', duration: 3000 });
      cancelarD.onClose();
      refrescar();
    } catch (error: any) {
      toast({ title: 'Error', description: error?.response?.data?.message || 'No se pudo cancelar.', status: 'error', duration: 4000 });
    } finally {
      setProcesando(false);
    }
  };

  const handleAtender = async (c: ImmCita) => {
    try {
      await atenderCita(c.id);
      toast({ title: 'Cita marcada como atendida', status: 'success', duration: 3000 });
      refrescar();
    } catch (error: any) {
      toast({ title: 'Error', description: error?.response?.data?.message || 'No se pudo actualizar.', status: 'error', duration: 4000 });
    }
  };

  // paginación tabla próximas
  const totalPages = Math.ceil(proximas.length / itemsPerPage);
  const indexUltimo = currentPage * itemsPerPage;
  const indexPrimero = indexUltimo - itemsPerPage;
  const itemsPagina = proximas.slice(indexPrimero, indexUltimo);

  return (
    <Box>
      {/* Barra de navegación de fecha */}
      <Flex mb={4} justify="space-between" align="center" flexWrap="wrap" gap={3}>
        <HStack spacing={2}>
          <IconButton aria-label="Día anterior" icon={<ChevronLeftIcon />} size="sm"
            onClick={() => setFecha((f) => shiftDay(f, -1))} />
          <Input type="date" value={fecha} onChange={(e) => setFecha(e.target.value || hoy())} size="sm" w="170px" />
          <IconButton aria-label="Día siguiente" icon={<ChevronRightIcon />} size="sm"
            onClick={() => setFecha((f) => shiftDay(f, 1))} />
          <Button size="sm" variant="ghost" onClick={() => setFecha(hoy())}>Hoy</Button>
        </HStack>
        <Button size="sm" leftIcon={<AddIcon />} bg={IMM_COLOR} color="white" _hover={{ bg: IMM_COLOR_HOVER }} onClick={nuevaD.onOpen}>
          Nueva cita
        </Button>
      </Flex>

      <Text fontSize="sm" color="gray.600" fontWeight="medium" textTransform="capitalize" mb={3}>
        {fechaLarga(fecha)}
      </Text>

      <SimpleGrid columns={{ base: 1, lg: 3 }} spacing={4}>
        {/* Agenda del día */}
        <Box gridColumn={{ lg: 'span 2' }}>
          {cargando ? (
            <Flex justify="center" py={12}><Spinner color={IMM_COLOR} size="lg" /></Flex>
          ) : citasOrdenadas.length === 0 ? (
            <VStack py={12} color="gray.400" spacing={2}>
              <Icon as={MdEventNote} boxSize={10} />
              <Text>No hay citas programadas para este día</Text>
            </VStack>
          ) : (
            <VStack spacing={3} align="stretch">
              {citasOrdenadas.map((c) => (
                <Box key={c.id} borderWidth="1px" borderRadius="md" boxShadow="sm" bg="white"
                  borderLeft={`3px solid ${IMM_COLOR}`} p={4} _hover={{ bg: 'red.50' }}>
                  <Flex justify="space-between" align="flex-start" flexWrap="wrap" gap={2}>
                    <Box>
                      <HStack spacing={2} mb={1}>
                        <Icon as={MdAccessTime} color={IMM_COLOR} />
                        <Text fontWeight="bold" color={IMM_COLOR}>
                          {c.horaInicio || '—'}{c.horaFin ? ` - ${c.horaFin}` : ''}
                        </Text>
                        <EstatusBadge value={c.estatus} />
                        <Tag size="sm" variant="subtle" colorScheme="gray">{c.origen}</Tag>
                      </HStack>
                      <Text fontSize="sm"><strong>Psicólogo/a:</strong> {c.psicologo || '—'}</Text>
                      <Text fontSize="sm"><strong>Motivo:</strong> {c.motivo || '—'}</Text>
                      {c.expedienteId && (
                        <Text fontSize="xs" color="gray.500">Expediente #{c.expedienteId}</Text>
                      )}
                    </Box>
                  </Flex>
                  <Divider my={3} />
                  <Wrap spacing={2}>
                    <WrapItem>
                      <Button size="xs" bg={IMM_COLOR} color="white" _hover={{ bg: IMM_COLOR_HOVER }}
                        onClick={() => { setSelected(c); sesionD.onOpen(); }}>
                        Registrar sesión
                      </Button>
                    </WrapItem>
                    <WrapItem>
                      <Button size="xs" colorScheme="purple" variant="outline"
                        isDisabled={c.estatus === 'Cancelada' || c.estatus === 'Atendida'}
                        onClick={() => abrirReagendar(c)}>
                        Reagendar
                      </Button>
                    </WrapItem>
                    <WrapItem>
                      <Button size="xs" colorScheme="green" variant="outline"
                        isDisabled={c.estatus === 'Cancelada' || c.estatus === 'Atendida'}
                        onClick={() => handleAtender(c)}>
                        Atendida
                      </Button>
                    </WrapItem>
                    <WrapItem>
                      <Button size="xs" colorScheme="red" variant="outline"
                        isDisabled={c.estatus === 'Cancelada' || c.estatus === 'Atendida'}
                        onClick={() => { setSelected(c); cancelarD.onOpen(); }}>
                        Cancelar
                      </Button>
                    </WrapItem>
                  </Wrap>
                </Box>
              ))}
            </VStack>
          )}
        </Box>

        {/* Panel de disponibilidad */}
        <Box borderWidth="1px" borderRadius="md" boxShadow="sm" bg="white" p={4} borderLeft={`3px solid ${IMM_COLOR}`} h="fit-content">
          <Text fontWeight="bold" color={IMM_COLOR} mb={3}>Disponibilidad</Text>
          {cargando ? (
            <Flex justify="center" py={6}><Spinner color={IMM_COLOR} /></Flex>
          ) : (
            <VStack align="stretch" spacing={4}>
              <Box>
                <Text fontSize="xs" color="gray.500" fontWeight="bold" mb={2}>HORARIOS LIBRES</Text>
                {disp && disp.horariosLibres.length > 0 ? (
                  <Wrap spacing={2}>
                    {disp.horariosLibres.map((h) => (
                      <WrapItem key={h}><Tag colorScheme="green" size="sm">{h}</Tag></WrapItem>
                    ))}
                  </Wrap>
                ) : (
                  <Text fontSize="sm" color="gray.400">Sin horarios libres</Text>
                )}
              </Box>
              <Box>
                <Text fontSize="xs" color="gray.500" fontWeight="bold" mb={2}>HORARIOS OCUPADOS</Text>
                {disp && disp.horariosOcupados.length > 0 ? (
                  <Wrap spacing={2}>
                    {disp.horariosOcupados.map((h) => (
                      <WrapItem key={h}><Tag colorScheme="red" size="sm">{h}</Tag></WrapItem>
                    ))}
                  </Wrap>
                ) : (
                  <Text fontSize="sm" color="gray.400">Sin horarios ocupados</Text>
                )}
              </Box>
              <Divider />
              <HStack spacing={4} fontSize="xs" color="gray.500">
                <HStack spacing={1}><Box w={3} h={3} bg="green.400" borderRadius="sm" /><Text>Libre</Text></HStack>
                <HStack spacing={1}><Box w={3} h={3} bg="red.400" borderRadius="sm" /><Text>Ocupado</Text></HStack>
              </HStack>
            </VStack>
          )}
        </Box>
      </SimpleGrid>

      {/* Tabla secundaria: próximas citas */}
      <Box mt={8}>
        <Text fontWeight="bold" color={IMM_COLOR} mb={3}>Todas las citas</Text>
        <Box overflowX="auto" borderRadius="md" boxShadow="md" bg="white">
          <Table variant="simple" size="sm">
            <Thead bg={IMM_COLOR}>
              <Tr>
                <Th color="white" fontSize="xs">Fecha</Th>
                <Th color="white" fontSize="xs">Horario</Th>
                <Th color="white" fontSize="xs">Psicólogo/a</Th>
                <Th color="white" fontSize="xs">Motivo</Th>
                <Th color="white" fontSize="xs">Origen</Th>
                <Th color="white" fontSize="xs">Estatus</Th>
              </Tr>
            </Thead>
            <Tbody>
              {itemsPagina.length === 0 ? (
                <Tr><Td colSpan={6} textAlign="center" py={8}><Text color="gray.400">No hay citas registradas</Text></Td></Tr>
              ) : (
                itemsPagina.map((c, idx) => (
                  <Tr key={c.id} bg={idx % 2 === 0 ? 'white' : 'gray.50'} _hover={{ bg: 'red.50' }}>
                    <Td>{fmt(c.fecha)}</Td>
                    <Td>{c.horaInicio || '—'}{c.horaFin ? ` - ${c.horaFin}` : ''}</Td>
                    <Td>{c.psicologo || '—'}</Td>
                    <Td><Text noOfLines={1} fontSize="sm">{c.motivo || '—'}</Text></Td>
                    <Td><Tag size="sm" variant="subtle" colorScheme="gray">{c.origen}</Tag></Td>
                    <Td><EstatusBadge value={c.estatus} /></Td>
                  </Tr>
                ))
              )}
            </Tbody>
          </Table>
        </Box>
        {totalPages > 1 && (
          <Flex mt={4} justify="space-between" align="center">
            <Text fontSize="sm" color="gray.500">Mostrando {indexPrimero + 1}–{Math.min(indexUltimo, proximas.length)} de {proximas.length}</Text>
            <HStack spacing={1}>
              <Button size="xs" onClick={() => setCurrentPage(1)} isDisabled={currentPage === 1}>«</Button>
              <Button size="xs" onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} isDisabled={currentPage === 1}>‹</Button>
              {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                const page = Math.max(1, currentPage - 2) + i;
                if (page > totalPages) return null;
                return (
                  <Button key={page} size="xs" bg={currentPage === page ? IMM_COLOR : undefined} color={currentPage === page ? 'white' : undefined} onClick={() => setCurrentPage(page)}>{page}</Button>
                );
              })}
              <Button size="xs" onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} isDisabled={currentPage === totalPages}>›</Button>
              <Button size="xs" onClick={() => setCurrentPage(totalPages)} isDisabled={currentPage === totalPages}>»</Button>
            </HStack>
          </Flex>
        )}
      </Box>

      {/* Modal nueva cita */}
      <Modal isOpen={nuevaD.isOpen} onClose={nuevaD.onClose} size="2xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader borderBottom="1px solid" borderColor="gray.200" color={IMM_COLOR}>Nueva cita</ModalHeader>
          <ModalCloseButton />
          <ModalBody py={5}>
            <FormularioCita modo="new" defaultFecha={fecha} onGuardado={handleGuardado} onCancelar={nuevaD.onClose} />
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Modal sesión */}
      {selected && (
        <ModalSesion isOpen={sesionD.isOpen} onClose={sesionD.onClose} cita={selected} onGuardado={refrescar} />
      )}

      {/* Modal reagendar */}
      <Modal isOpen={reagendarD.isOpen} onClose={reagendarD.onClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader borderBottom="1px solid" borderColor="gray.200" color={IMM_COLOR}>Reagendar cita</ModalHeader>
          <ModalCloseButton />
          <ModalBody py={5}>
            <VStack spacing={3} align="stretch">
              <FormControl>
                <FormLabel fontSize="sm">Nueva fecha</FormLabel>
                <Input type="date" value={rea.fecha} onChange={(e) => setRea((p) => ({ ...p, fecha: e.target.value }))} />
              </FormControl>
              <HStack spacing={3}>
                <FormControl>
                  <FormLabel fontSize="sm">Hora inicio</FormLabel>
                  <Input type="time" value={rea.horaInicio} onChange={(e) => setRea((p) => ({ ...p, horaInicio: e.target.value }))} />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">Hora fin</FormLabel>
                  <Input type="time" value={rea.horaFin} onChange={(e) => setRea((p) => ({ ...p, horaFin: e.target.value }))} />
                </FormControl>
              </HStack>
              <FormControl>
                <FormLabel fontSize="sm">Psicólogo/a</FormLabel>
                <Input value={rea.psicologo} onChange={(e) => setRea((p) => ({ ...p, psicologo: e.target.value }))} />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="outline" mr={3} onClick={reagendarD.onClose} isDisabled={procesando}>Cancelar</Button>
            <Button bg={IMM_COLOR} color="white" _hover={{ bg: IMM_COLOR_HOVER }} onClick={handleReagendar} isLoading={procesando} loadingText="Guardando...">
              Reagendar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* AlertDialog cancelar */}
      <AlertDialog isOpen={cancelarD.isOpen} leastDestructiveRef={cancelRef} onClose={cancelarD.onClose}>
        <AlertDialogOverlay><AlertDialogContent>
          <AlertDialogHeader fontSize="lg" fontWeight="bold" color={IMM_COLOR}>Cancelar cita</AlertDialogHeader>
          <AlertDialogBody>
            ¿Deseas cancelar la cita del <strong>{selected ? fmt(selected.fecha) : ''}</strong>
            {selected?.horaInicio ? ` a las ${selected.horaInicio}` : ''}? Esta acción marcará la cita como cancelada.
          </AlertDialogBody>
          <AlertDialogFooter>
            <Button ref={cancelRef} onClick={cancelarD.onClose} isDisabled={procesando}>Volver</Button>
            <Button colorScheme="red" onClick={handleCancelarConfirmar} ml={3} isLoading={procesando} loadingText="Procesando...">Cancelar cita</Button>
          </AlertDialogFooter>
        </AlertDialogContent></AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default CalendarioPsicologia;
